// src/lib/storeRealtime.ts
// ─────────────────────────────────────────────────────────────
// TIENDA DIGITAL EN TIEMPO REAL (Supabase Realtime)
//
//   * Catálogo y stock desde la NUBE (`supplier_products` + `products`).
//   * Suscripción Realtime: si en el POS cambian precio, stock, estado del
//     producto o la suscripción Naranja Plus, la tienda se actualiza al
//     instante SIN recargar.
//   * Pedido web → `public.web_orders` (el POS lo recibe por Realtime).
// ─────────────────────────────────────────────────────────────

import { db } from '@/db/db';
import { supabase } from '@/lib/supabase';

const sb = () => supabase as any;

/** Normaliza un texto a slug (para enlaces amigables de tienda). */
export const slugifyStore = (value: string): string =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/**
 * RESUELVE EL `business_id` REAL a partir del parámetro de la URL, que puede
 * ser el ID del negocio O un slug/nombre amigable (ej: /tienda/mi-tienda).
 *
 * Orden: local por business_id → local por slug/nombre → tabla `businesses`
 * → nube por business_id → nube por slug. Devuelve null si no existe.
 */
export const resolveStoreBusinessId = async (raw: string): Promise<string | null> => {
  const param = String(raw || '').trim();
  if (!param) return null;
  const needle = slugifyStore(param);

  // ── LOCAL ──
  try {
    const byId = await db.settings.where('business_id').equals(param).first();
    if (byId?.business_id) return String(byId.business_id);
  } catch { /* sigue con las otras estrategias */ }

  try {
    const all = await db.settings.toArray();
    const match = (all as any[]).find((s) => {
      const candidates = [s.store_slug, s.slug, s.store_name, s.business_name, s.business_id];
      return candidates.some((c) => c && (String(c) === param || slugifyStore(String(c)) === needle));
    });
    if (match?.business_id) {
      console.log(`🏪 [TIENDA] "${param}" resuelto localmente a business_id=${match.business_id}`);
      return String(match.business_id);
    }
  } catch { /* sin catálogo local */ }

  try {
    const biz = await db.table('businesses').where('slug').equals(param).first() as any;
    if (biz?.id) return String(biz.id);
  } catch { /* tabla opcional */ }

  // Proveedor (catálogo B2B): su slug resuelve al negocio enlazado.
  try {
    const supplier = await db.suppliers.where('slug').equals(param).first() as any;
    if (supplier) return String(supplier.linked_business_id || supplier.id);
  } catch { /* tabla opcional */ }

  // ── NUBE ──
  if (supabase) {
    try {
      const { data } = await sb().from('settings').select('business_id').eq('business_id', param).maybeSingle();
      if (data?.business_id) return String(data.business_id);
    } catch { /* columna/red */ }

    // Slug de tienda (columnas opcionales en public.settings).
    for (const column of ['store_slug', 'slug']) {
      try {
        const { data } = await sb().from('settings').select('business_id').eq(column, param).maybeSingle();
        if (data?.business_id) {
          console.log(`🏪 [TIENDA] "${param}" resuelto en la nube por ${column} → ${data.business_id}`);
          return String(data.business_id);
        }
      } catch { /* columna inexistente: se ignora */ }
    }

    try {
      const { data } = await sb().from('businesses').select('id').eq('slug', param).maybeSingle();
      if (data?.id) return String(data.id);
    } catch { /* tabla opcional */ }
  }

  console.warn(`🏪 [TIENDA] No se pudo resolver "${param}" a un business_id.`);
  return null;
};

export interface StoreOrderInput {
  id: string;
  business_id: string;
  customer_name: string;
  customer_phone: string;
  customer_address?: string | null;
  customer_notes?: string | null;
  delivery_type: string;
  payment_method: string;
  items: any[];
  total: number;
  source?: 'STORE' | 'SUPPLIER_CATALOG';
}

/** Catálogo del negocio desde la NUBE (para la tienda pública). */
export const fetchStoreCatalogCloud = async (businessId: string): Promise<any[]> => {  if (!supabase || !businessId) return [];
  try {
    const [supplierProducts, generalProducts] = await Promise.all([
      sb().from('supplier_products').select('*').eq('business_id', businessId).eq('is_active', true),
      sb().from('products').select('*').eq('business_id', businessId).eq('is_active', true),
    ]);
    const merged = new Map<string, any>();
    for (const row of (generalProducts.data || [])) if (row?.id) merged.set(String(row.id), { ...row, price_unit: row.price });
    for (const row of (supplierProducts.data || [])) {
      if (!row?.id) continue;
      merged.set(String(row.id), { ...row, price: row.price_unit ?? row.price, price_unit: row.price_unit ?? row.price });
    }
    return Array.from(merged.values());
  } catch (e) {
    console.warn('Tienda: no se pudo leer el catálogo de la nube:', e);
    return [];
  }
};

/** Guarda el catálogo de la nube en el caché local (para funcionar offline). */
export const cacheStoreCatalog = async (products: any[]): Promise<void> => {
  if (!products || products.length === 0) return;
  try {
    for (const p of products) {
      if (!p?.id) continue;
      const existing = await db.products.get(p.id);
      if (existing) {
        await db.products.update(p.id, {
          price: Number(p.price ?? p.price_unit ?? existing.price ?? 0),
          stock_current: Number(p.stock_current ?? p.stock ?? existing.stock_current ?? 0),
          is_active: p.is_active !== false,
          name: p.name || existing.name,
          image_url: p.image_url ?? existing.image_url,
        });
      } else {
        await db.products.add({
          id: String(p.id),
          business_id: String(p.business_id || 'local'),
          name: p.name || 'Producto',
          price: Number(p.price ?? p.price_unit ?? 0),
          cost: 0,
          stock_current: Number(p.stock_current ?? p.stock ?? 0),
          stock_min: 0,
          unit: 'UNIDAD',
          type: 'FISICO',
          is_active: p.is_active !== false,
          image_url: p.image_url ?? null,
          created_at: p.created_at || new Date().toISOString(),
        } as any);
      }
    }
  } catch (e) {
    console.warn('Tienda: no se pudo cachear el catálogo:', e);
  }
};

/**
 * Realtime de la tienda: catálogo (`products`, `supplier_products`) y la
 * suscripción Naranja Plus del negocio.
 * @returns función para cancelar todas las suscripciones
 */
export const subscribeToStoreRealtime = (
  businessId: string,
  handlers: { onCatalogChange?: () => void; onSubscriptionChange?: (status: string) => void },
): (() => void) => {
  if (!supabase || !businessId) return () => {};
  const channels: any[] = [];

  const listen = (table: string) => {
    try {
      const channel = sb()
        .channel(`niiz_store_${table}_${businessId}`)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table, filter: `business_id=eq.${businessId}` },
          () => { handlers.onCatalogChange?.(); },
        )
        .subscribe();
      channels.push(channel);
    } catch (e) {
      console.warn(`Tienda: no se pudo suscribir a ${table}:`, e);
    }
  };

  listen('products');
  listen('supplier_products');

  try {
    const subChannel = sb()
      .channel(`niiz_store_naranja_${businessId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'naranja_plus', filter: `business_id=eq.${businessId}` },
        (payload: any) => {
          const row = payload?.new || payload?.record;
          if (row?.status) handlers.onSubscriptionChange?.(String(row.status));
        },
      )
      .subscribe();
    channels.push(subChannel);
  } catch (e) {
    console.warn('Tienda: no se pudo suscribir a la suscripción:', e);
  }

  return () => {
    channels.forEach(c => { try { sb().removeChannel(c); } catch { /* noop */ } });
  };
};

/**
 * Crea el pedido web: SIEMPRE en local (Dexie) y en la NUBE si hay conexión.
 * La nube es la que dispara la campana del POS por Realtime.
 */
export const createWebOrder = async (input: StoreOrderInput): Promise<{ ok: boolean; cloud: boolean; error?: string }> => {
  // 1. Dexie (la tienda sigue operando sin internet)
  try {
    await db.store_orders.add({
      id: input.id,
      business_id: input.business_id,
      customer_name: input.customer_name,
      customer_phone: input.customer_phone,
      ...(input.customer_address ? { customer_address: input.customer_address } : {}),
      ...(input.customer_notes ? { customer_notes: input.customer_notes } : {}),
      delivery_type: input.delivery_type,
      payment_method: input.payment_method,
      items_json: JSON.stringify(input.items),
      total: input.total,
      status: 'PENDING',
      created_at: new Date().toISOString(),
      is_read: 0,
    } as any);
  } catch (e: any) {
    console.error('❌ [TIENDA] No se pudo guardar el pedido en local:', e?.message || e);
  }

  // 2. Nube (para que el POS del negocio lo vea al instante)
  if (!supabase) return { ok: true, cloud: false };

  try {
    const { error } = await sb().from('web_orders').upsert({
      id: input.id,
      business_id: input.business_id,
      customer_name: input.customer_name,
      customer_phone: input.customer_phone,
      customer_address: input.customer_address ?? null,
      customer_notes: input.customer_notes ?? null,
      delivery_type: input.delivery_type,
      payment_method: input.payment_method,
      items_json: input.items,
      total: input.total,
      status: 'PENDING',
      source: input.source || 'STORE',
      is_read: false,
      created_at: new Date().toISOString(),
    }, { onConflict: 'id' });

    if (error) {
      console.warn('Tienda: el pedido quedó en local, nube pendiente:', error.message);
      return { ok: true, cloud: false, error: error.message };
    }
    console.log('🛒 [TIENDA] Pedido publicado en la nube:', input.id);
    return { ok: true, cloud: true };
  } catch (e: any) {
    return { ok: true, cloud: false, error: e?.message || 'Sin conexión con la nube' };
  }
};

/**
 * Campana del POS: escucha en Realtime los pedidos web del negocio.
 * @returns función para cancelar la suscripción
 */
export const subscribeToWebOrders = (
  businessId: string,
  onNewOrder: (order: any) => void,
): (() => void) => {
  if (!supabase || !businessId) return () => {};
  try {
    const channel = sb()
      .channel(`niiz_web_orders_${businessId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'web_orders', filter: `business_id=eq.${businessId}` },
        (payload: any) => {
          const row = payload?.new || payload?.record;
          if (row?.id) onNewOrder(row);
        },
      )
      .subscribe();
    return () => { try { sb().removeChannel(channel); } catch { /* noop */ } };
  } catch (e) {
    console.warn('POS: no se pudo suscribir a los pedidos web:', e);
    return () => {};
  }
};

/** Pedidos web pendientes del negocio (para la campana). */
export const getPendingWebOrders = async (businessId: string): Promise<any[]> => {
  const local = await db.store_orders.where('business_id').equals(businessId).toArray().catch(() => []);
  const pendingLocal = (local as any[]).filter(o => !o.is_read);

  if (!supabase) return pendingLocal;
  try {
    const { data, error } = await sb()
      .from('web_orders')
      .select('*')
      .eq('business_id', businessId)
      .eq('status', 'PENDING')
      .order('created_at', { ascending: false })
      .limit(50);
    if (error || !data) return pendingLocal;
    return data as any[];
  } catch {
    return pendingLocal;
  }
};

/** Marca un pedido web como leído/atendido. */
export const markWebOrderRead = async (orderId: string): Promise<void> => {
  try {
    await db.store_orders.update(orderId, { is_read: 1 } as any);
  } catch { /* puede no existir en local */ }
  if (!supabase) return;
  try {
    await sb().from('web_orders').update({ is_read: true, status: 'CONFIRMED' }).eq('id', orderId);
  } catch (e) {
    console.warn('No se pudo marcar el pedido web:', e);
  }
};
